import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { dateOnlyFromTimestampSP, daysUntilDateOnly } from "@/lib/date-utils";

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["subscription", user?.id],
    enabled: !!user && !authLoading,
    staleTime: 60_000,
    queryFn: async () => {
      const [profileRes, acessoRes] = await Promise.all([
        supabase
          .from("profiles")
          .select(
            "subscription_status, lifetime_access, subscription_end_date, subscription_plan, stripe_subscription_id",
          )
          .eq("user_id", user!.id)
          .maybeSingle(),
        supabase.rpc("usuario_tem_assinatura_ativa"),
      ]);

      if (profileRes.error) throw profileRes.error;
      if (acessoRes.error) throw acessoRes.error;

      return { profile: profileRes.data, temAcesso: acessoRes.data === true };
    },
  });

  const profile = data?.profile;
  const status = profile?.subscription_status ?? null;
  const hasLifetime = profile?.lifetime_access === true;
  const inTrial = status === "trialing";
  const hasPaid = status === "active";
  const everSubscribed = !!profile?.stripe_subscription_id;

  const endDate = profile?.subscription_end_date
    ? new Date(profile.subscription_end_date)
    : null;
  const trialDaysLeft: number | null = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86_400_000))
    : null;

  // Teste de 14 dias sem cartão (plano 'teste') ou cortesia: trialing sem Stripe.
  const plan = profile?.subscription_plan ?? null;
  const isTesteSemCartao =
    inTrial && !everSubscribed && (plan === "teste" || plan === "cortesia");
  const diasTesteRestantes: number | null = isTesteSemCartao
    ? (() => {
        const d = daysUntilDateOnly(dateOnlyFromTimestampSP(profile?.subscription_end_date));
        return d === null ? null : Math.max(0, d);
      })()
    : null;
  // Já teve o teste sem cartão e ele venceu (cron marcou canceled).
  const testeTerminou =
    status === "canceled" && !everSubscribed && (plan === "teste" || plan === "cortesia");

  return {
    status,
    isTesteSemCartao,
    diasTesteRestantes,
    testeTerminou,
    endDate: profile?.subscription_end_date ?? null,
    plan: profile?.subscription_plan ?? null,
    // Testadora em cortesia: sem cartão nem assinatura no Stripe, nada é cobrado.
    isCortesia: profile?.subscription_plan === "cortesia",
    // A assinatura é da EMPRESA: o acesso vem da função SQL
    // usuario_tem_assinatura_ativa, que também aplica a regra dos
    // Termos 9.3 (past_due tem aviso, sem bloqueio imediato).
    isActive: data?.temAcesso === true,
    inTrial,
    trialDaysLeft,
    hasPaid,
    hasLifetime,
    everSubscribed,
    loading: authLoading || (!!user && isLoading),
  };
}
